import { Link } from 'react-router-dom';
import { hotVideos } from '@/mocks/homeData';
import AnimatedSection from '@/components/base/AnimatedSection';

export default function VideoGridSection() {
  return (
    <section id="video-grid" className="relative py-20 md:py-28 bg-background-50">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6">
        <AnimatedSection>
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <h2 className="text-[clamp(2rem,4vw,3.5rem)] font-black text-foreground-50 tracking-[-0.02em] leading-[1.1]">
              最新猛料
            </h2>
            <p className="text-sm text-foreground-600 mt-3 max-w-md">
              实时追踪娱乐圈最新动态，每一条都是新鲜出炉的狠料
            </p>
          </div>
          <Link
            to="/category/mingxing-bagua"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap cursor-pointer"
          >
            查看全部
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </span>
          </Link>
        </div>
        </AnimatedSection>

        <AnimatedSection animation="fade-up">
        {/* Video grid - 3 columns on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Featured large card */}
          <div className="md:col-span-2 lg:col-span-1 lg:row-span-2">
            <VideoFeatureCard video={hotVideos[0]} />
          </div>

          {/* Regular cards */}
          {hotVideos.slice(1).map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

function VideoFeatureCard({ video }: { video: typeof hotVideos[0] }) {
  return (
    <Link
      to={`/video/${video.id}`}
      className="group block relative h-full min-h-[500px] rounded-2xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-500/40 transition-all duration-300 cursor-pointer"
    >
      {/* Cover image */}
      <div className="absolute inset-0">
        <img
          src={video.coverImage}
          alt={video.title}
          title={`黑料视频 - ${video.title}`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent" />
      </div>

      {/* Content overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {video.isHot && (
            <span className="px-2.5 py-1 bg-primary-500 text-background-950 text-[11px] font-bold rounded-full whitespace-nowrap">
              HOT
            </span>
          )}
          {video.isNew && (
            <span className="px-2.5 py-1 bg-accent-500 text-background-950 text-[11px] font-bold rounded-full whitespace-nowrap">
              NEW
            </span>
          )}
          <span className="px-2.5 py-1 bg-background-200/80 text-foreground-400 text-[11px] font-medium rounded-full whitespace-nowrap">
            {video.category}
          </span>
        </div>
        <h3 className="text-xl font-bold text-foreground-50 mb-3 group-hover:text-primary-400 transition-colors line-clamp-2">
          {video.title}
        </h3>
        <div className="flex items-center gap-4 text-xs text-foreground-600">
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-eye-line"></i>
            </span>
            {video.views}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-chat-3-line"></i>
            </span>
            {video.comments.toLocaleString()}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-time-line"></i>
            </span>
            {video.duration}
          </span>
        </div>
      </div>
    </Link>
  );
}

function VideoCard({ video }: { video: typeof hotVideos[0] }) {
  return (
    <Link
      to={`/video/${video.id}`}
      className="group block rounded-2xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-500/40 transition-all duration-300 cursor-pointer"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={video.coverImage}
          alt={video.title}
          title={`黑料视频 - ${video.title}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/60 to-transparent" />
        {/* Duration badge */}
        <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-background-950/80 text-foreground-200 text-[11px] font-medium rounded-md whitespace-nowrap">
          {video.duration}
        </span>
        {/* Status badges */}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {video.isHot && (
            <span className="px-2 py-0.5 bg-primary-500 text-background-950 text-[10px] font-bold rounded-full whitespace-nowrap">
              HOT
            </span>
          )}
          {video.isNew && (
            <span className="px-2 py-0.5 bg-accent-500 text-background-950 text-[10px] font-bold rounded-full whitespace-nowrap">
              NEW
            </span>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <span className="text-[11px] text-primary-400 font-medium mb-1.5 block">
          {video.category}
        </span>
        <h3 className="text-sm font-semibold text-foreground-100 mb-3 group-hover:text-primary-400 transition-colors line-clamp-2 leading-snug">
          {video.title}
        </h3>
        <div className="flex items-center gap-3 text-xs text-foreground-600">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-eye-line"></i>
            </span>
            {video.views}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-chat-3-line"></i>
            </span>
            {video.comments.toLocaleString()}
          </span>
          <span className="ml-auto text-foreground-700">{video.publishTime}</span>
        </div>
      </div>
    </Link>
  );
}